import { useEffect, useState, useCallback } from "react";
import { IoIosLink } from "react-icons/io";
import { toast, ToastContainer } from "react-toastify";
import LocationService from "../../../components/Services/LocationService";
import ProspectPropertyService from "../../Service/ProspectPropertyService";
import CustomSelect from "../../../components/common/CustomSelect";
import Accordion from "../../../components/SetupComponents/PropertiesComponents/Accordion/Accordion";
import { showInfoToast, showErrorToast } from "../../../components/Constant/Notification";
import MediaUpload from "../../../components/SetupComponents/PropertiesComponents/MediaUpload";
import UserService from "../../../GlobalSetupComponents/Services/UserService";
import { propertyParitalTypes } from "../../Enum/PropertyEnum";
import { roleIdProcurement } from "../../../GlobalSetupComponents/Enum/RoleEnum";
import CustomPropertySelect from "../../../components/common/CustomPropertySelect";
import RoughPropertyService from "../../Service/RoughPropertyService";
import MapWithPropertyAndData from "./MapWithPropertyAndData";
import PhoneInputField from "../../../components/common/PhoneInputField";


const RoughPropertyInfoBasic = ({
  propertyInfo,
  setPropertyInfo,
  handlePropertyInfoChange,
  handleArrayFieldChange,
  isEdit,
  propertyType = "layout", // Options: "layout", "acresAndCents", "resalePlot"
}) => {
  const [locationData, setLocationData] = useState([]);
  const [propertyNameOptions, setPropertyNameOptions] = useState([]);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [inputValue, setInputValue] = useState(propertyInfo?.name || "");
  const [sourceBy, setSourceBy] = useState([]);
  

  const SQFT_TO_CENT = 435.6;
  const SQFT_TO_ACRE = 43560;
  const SQFT_TO_GROUND = 2400;

  const getDerivedAreas = (value, inputField) => {
    const numValue = parseFloat(value) || 0;
    let sqftValue = 0;

    // Convert input value to square feet based on the input field
    switch (inputField) {
      case "overAllSqft":
        sqftValue = numValue;
        break;
      case "overAllCent":
        sqftValue = numValue * SQFT_TO_CENT;
        break;
      case "overAllAcre":
        sqftValue = numValue * SQFT_TO_ACRE;
        break;
      case "overAllGround":
        sqftValue = numValue * SQFT_TO_GROUND;
        break;
      default:
        sqftValue = numValue;
    }

    // Calculate derived values for all units with 4 decimal places
    return {
      overAllSqft: parseFloat(sqftValue.toFixed(4)),
      overAllCent: parseFloat((sqftValue / SQFT_TO_CENT).toFixed(4)),
      overAllAcre: parseFloat((sqftValue / SQFT_TO_ACRE).toFixed(4)),
      overAllGround: parseFloat((sqftValue / SQFT_TO_GROUND).toFixed(4))
    };
  };

  useEffect(() => {
    const fetchLocationData = async () => {
      try {
        const response = await LocationService.fetchLocation();
        const rawData = response.data || {};
        const formattedLocations = Object.entries(rawData).map(([name, id]) => ({
          value: id,
          label: name,
        }));
        setLocationData(formattedLocations);
      } catch (error) {
        showErrorToast("Failed to load locations");
      }
    };
    fetchLocationData();
  }, []);

  useEffect(() => {
    const fetchUserRoleBasedData = async () => {
      try {
        const body = { roleIds: roleIdProcurement };
        const response = await UserService.getUserRoleBasedNames(body);
        const data = response.data?.users || [];
        setSourceBy(data);
      } catch (error) {
        showErrorToast("Failed to fetch user role-based data");
      }
    };
    fetchUserRoleBasedData();
  }, []);

  useEffect(() => {
    const fetchNames = async () => {
      try {
        const body ={
          searchKey : inputValue,
          collection : "roughProperties"
        }
        const data = await RoughPropertyService.searchProperties(body);
        const options =
        data?.data?.data?.map((item) => ({
          value: item.id,       
          label: item.name,
          plotNo: item.plotNo,
          rate: item.rate,
          sourcedBy: item.propertySourcedBy,
          status: item.status,
        })) || [];
        setPropertyNameOptions(options);
        setFilteredOptions(options);
      } catch (error) {
       
        setPropertyNameOptions([]);
        setFilteredOptions([]);
      }
    };
    fetchNames();
  }, [inputValue]);

  // Auto-calculate rate when relevant fields change
  useEffect(() => {
    if (propertyInfo?.areaRate && (propertyInfo?.overAllSqft || propertyInfo?.overAllCent || propertyInfo?.overAllAcre || propertyInfo?.overAllGround)) {
      calculateRateInNumber();
    }
  }, [propertyInfo?.areaRate, propertyInfo?.overAllSqft, propertyInfo?.overAllCent, propertyInfo?.overAllAcre, propertyInfo?.overAllGround, propertyInfo?.areaType]);

  const handlePropertyNameInputChange = useCallback(
    (inputValue, actionMeta) => {
      setInputValue(inputValue);
      if (actionMeta.action === "input-change") {
        handlePropertyInfoChange({
          target: { name: "name", value: inputValue },
        });
        const filtered = propertyNameOptions.filter((option) =>
          option.label.toLowerCase().includes(inputValue.toLowerCase())
        );
        const optionsToShow = [...filtered];
        if (
          inputValue.trim() &&
          !propertyNameOptions.some(
            (option) => option.label.toLowerCase() === inputValue.toLowerCase()
          )
        ) {
          optionsToShow.push({
            value: inputValue.trim(),
            label: `Create "${inputValue.trim()}"`,
            isCustom: true,
          });
        }
        setFilteredOptions(optionsToShow);
      }
    },
    [propertyNameOptions, handlePropertyInfoChange]
  );

  const handlePropertyNameChange = (selectedOption) => {
    if (selectedOption) {
      handlePropertyInfoChange({
        target: { name: "name", value: selectedOption.label },
      });
      if (selectedOption.isCustom) {
        showInfoToast(`Added "${selectedOption.label}" as new property`);
      }
      setInputValue(selectedOption.value);
    } else {
      handlePropertyInfoChange({
        target: { name: "name", value: "" },
      });
      setInputValue("");
    }
  };

  const handleAreaChange = (e) => {
    const { name, value } = e.target;
    const numValue = parseFloat(value);

    if (value !== "" && (isNaN(numValue) || numValue < 0)) {
      showErrorToast("Area cannot be negative or invalid");
      return;
    }

    // Calculate derived areas based on the input field
    const derivedAreas = getDerivedAreas(value, name);

    // Update all area fields
    handlePropertyInfoChange({
      target: { name: "overAllSqft", value: derivedAreas.overAllSqft },
    });
    handlePropertyInfoChange({
      target: { name: "overAllCent", value: derivedAreas.overAllCent },
    });
    handlePropertyInfoChange({
      target: { name: "overAllAcre", value: derivedAreas.overAllAcre },
    });
    handlePropertyInfoChange({
      target: { name: "overAllGround", value: derivedAreas.overAllGround },
    });

    // Auto-calculate Rate in Number when area changes
    // Pass the updated area values directly to avoid state lag
    const updatedPropertyInfo = {
      ...propertyInfo,
      overAllSqft: derivedAreas.overAllSqft,
      overAllCent: derivedAreas.overAllCent,
      overAllAcre: derivedAreas.overAllAcre,
      overAllGround: derivedAreas.overAllGround,
    };
    
    setTimeout(() => {
      calculateRateInNumber(null, null, updatedPropertyInfo);
    }, 100);
  };

  const handleRateChange = (e) => {
    const { name, value } = e.target;
    if (value !== "" && parseFloat(value) < 0) {
      showErrorToast("Rate cannot be negative");
      return;
    }
    handlePropertyInfoChange({
      target: { name, value: value || "" },
    });
    
    // Auto-calculate Rate in Number when rate changes
    // Pass the updated rate directly to avoid state lag
    const updatedPropertyInfo = {
      ...propertyInfo,
      areaRate: value,
    };
    
    setTimeout(() => {
      calculateRateInNumber(value, null, updatedPropertyInfo);
    }, 100);
  };

  const calculateRateInNumber = (ratePerUnit = null, customAreaType = null, updatedPropertyInfo = null) => {
    // Use updated propertyInfo if provided, otherwise use current state
    const currentPropertyInfo = updatedPropertyInfo || propertyInfo;
    const rate = parseFloat(ratePerUnit || currentPropertyInfo?.areaRate) || 0;
    const selectedAreaType = customAreaType || currentPropertyInfo?.areaType || "sqft";
    
    let areaValue = 0;
    
    // Get the area value based on selected rate per unit type
    switch (selectedAreaType) {
      case "sqft":
        areaValue = parseFloat((parseFloat(currentPropertyInfo?.overAllSqft) || 0).toFixed(4));
        break;
      case "cent":
        areaValue = parseFloat((parseFloat(currentPropertyInfo?.overAllCent) || 0).toFixed(4));
        break;
      case "ground":
        areaValue = parseFloat((parseFloat(currentPropertyInfo?.overAllGround) || 0).toFixed(4));
        break;
      case "acres":
        areaValue = parseFloat((parseFloat(currentPropertyInfo?.overAllAcre) || 0).toFixed(4));
        break;
      default:
        areaValue = parseFloat((parseFloat(currentPropertyInfo?.overAllSqft) || 0).toFixed(4));
    }
    
   
    
    if (rate > 0 && areaValue > 0) {
      const totalRate = rate * areaValue;
      const formattedRate = totalRate
      
      console.log("Calculated total rate:", totalRate, "Formatted:", formattedRate); // Debug log
      
      handlePropertyInfoChange({
        target: { name: "rateInNumber", value: formattedRate },
      });
    } else {
      // Clear the rate in numbers if either rate or area is 0
      handlePropertyInfoChange({
        target: { name: "rateInNumber", value: "" },
      });
    }
  };

  // Get geo location label based on property type
  const getGeoLocationLabel = () => {
    switch (propertyType) {
      case "acresAndCents":
      case "resalePlot":
        return "Type Latitude and longitude";
      default:
        return "Geo Location";
    }
  };

  // Check if overall area range should be shown (only for acres/cents and resale plot)
  const shouldShowAreaRange = () => {
    return propertyType === "acresAndCents" || propertyType === "resalePlot";
  };

  const accordionItems = [
    {
      title: "Basic Info",
      content: (
        <div className="grid grid-cols-1 md:grid-cols-2 pb-32 gap-6 p-6">
          <div className="flex items-center gap-4">
            <label className="w-40 text-sm font-medium text-gray-700 flex items-center">
              <span className="w-1 h-6 bg-red-500 mr-2 rounded-sm"></span>
              Property Name
            </label>
            <div className="w-[72%]">
              <CustomPropertySelect
                value={
                  propertyInfo?.name
                    ? { label: propertyInfo.name, value: propertyInfo.name }
                    : null
                }
                options={filteredOptions}
                isClearable
                isSearchable
                onChange={handlePropertyNameChange}
                onInputChange={handlePropertyNameInputChange}
                inputValue={inputValue}
                placeholder="Search or Type Property Name"
                className="flex-1"
                noOptionsMessage={({ inputValue }) =>
                  inputValue
                    ? `No results found. Keep typing to add "${inputValue}"`
                    : "Start typing to search properties"
                }
                filterOption={() => true}
              />
            </div>
          </div>
          <div className="flex items-center gap-4">
            <label className="w-40 text-sm font-medium text-gray-700 flex items-center">
              Partial/Full
            </label>
            <CustomSelect
              options={propertyParitalTypes}
              value={
                propertyInfo?.purchaseType
                  ? { value: propertyInfo.purchaseType, label: propertyInfo.purchaseType }
                  : null
              }
              onChange={(selectedOption) =>
                handlePropertyInfoChange({
                  target: { name: "purchaseType", value: selectedOption?.value || "" },
                })
              }
              placeholder="Purchase Type"
              className="flex-1"
              isSearchable
            />
          </div>
          <div className="flex items-center gap-4">
            <label className="w-40 text-sm font-medium text-gray-700 flex items-center">
              Number of Units
            </label>
            <input
              type="number"
              name="noOfUnits"
              min={1}
              value={propertyInfo?.noOfUnits || ""}
              onChange={handlePropertyInfoChange}
              onWheel={(e) => e.target.blur()}
              className="flex-1 px-4 py-2 w-full input-focus no-arrow"
              placeholder="Enter Number of Units"
            />
          </div>
          <div className="flex items-center gap-4">
            <label className="w-40 text-sm font-medium text-gray-700 flex items-center">
              Property Entered Date
            </label>
            <input
              type="date"
              disabled={propertyInfo?.propertyEnteredDate ? isEdit : false}
              name="propertyEnteredDate"
              value={propertyInfo?.propertyEnteredDate || ""}
              onChange={handlePropertyInfoChange}
              className="flex-1 px-4 py-2 input-focus"
              placeholder="Select Date"
              max={new Date().toISOString().split("T")[0]}
            />
          </div>
        </div>
      ),
    },
    {
      title: "Property Area & Rate",
      content: (
        <div className="space-y-8 p-6">
          {/* Overall Area */}
          <div className="grid grid-cols-1 md:grid-cols-[160px_1fr] gap-6 items-start">
            <label className="text-sm font-medium text-gray-700 flex items-center">
              <span className="w-1 h-6 bg-red-500 mr-2 rounded-sm"></span>
              Overall Area
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { name: "overAllSqft", label: "SQ.FT" },
                { name: "overAllCent", label: "Cents" },
                { name: "overAllAcre", label: "Acres" },
                { name: "overAllGround", label: "Grounds" },
              ].map((field) => (
                <div key={field.name} className="relative">
                  <input
                    type="number"
                    name={field.name}
                    value={propertyInfo?.[field.name] || ""}
                    onChange={handleAreaChange}
                    onWheel={(e) => e.target.blur()}
                    placeholder={`Type ${field.label}`}
                    className="w-full px-4 py-2 input-focus no-arrow"
                    step="any"
                    onKeyDown={
                          (e) => {
                            if (e.key === "-" || e.key === "e" || e.key === "+") {
                              e.preventDefault();
                            }
                            if (e.key === "e") {
                              e.preventDefault();
                            }
                          } 
                        }
                  />
                  <span className="absolute right-2 top-2 text-xs text-blue-600 font-medium bg-blue-50 px-2 py-1 rounded">
                    {field.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Rate Per */}
          <div className="grid grid-cols-1 md:grid-cols-[160px_1fr] gap-6 items-center">
            <label className="text-sm font-medium text-gray-700 flex items-center">
              <span className="w-1 h-6 bg-red-500 mr-2 rounded-sm"></span>
              Rate Per
            </label>
            <div className="flex flex-col sm:flex-row gap-4 w-full">
              <div className="w-40">
                <CustomSelect
                  options={[
                    { value: "sqft", label: "SQ.FT" },
                    { value: "cent", label: "Cent" },
                    { value: "ground", label: "Ground" },
                    { value: "acres", label: "Acres" },
                  ]}
                  value={
                    propertyInfo?.areaType
                      ? {
                          value: propertyInfo.areaType,
                          label:
                            propertyInfo.areaType === "sqft"
                              ? "SQ.FT"
                              : propertyInfo.areaType.charAt(0).toUpperCase() +
                                propertyInfo.areaType.slice(1),
                        }
                      : { value: "sqft", label: "SQ.FT" }
                  }
                  onChange={(selectedOption) => {
                    const newAreaType = selectedOption?.value || "sqft";
                    handlePropertyInfoChange({
                      target: { name: "areaType", value: newAreaType },
                    });
                    // Recalculate rate when area type changes
                    setTimeout(() => {
                      calculateRateInNumber(null, newAreaType);
                    }, 0);
                  }}
                  placeholder="Select Unit"
                  isClearable={false}
                />
              </div>
              <input
                type="number"
                name="areaRate"
                value={propertyInfo?.areaRate || ""}
                onChange={handleRateChange}
                onWheel={(e) => e.target.blur()}
                className="flex-1 px-4 py-2 input-focus no-arrow"
                placeholder="Type Rate"
                min="0"
                step="any"
              />
            </div>
          </div>

          {/* Rate in Number - Auto Calculated */}
          <div className="grid grid-cols-1 md:grid-cols-[160px_1fr] gap-6 items-center">
            <label className="text-sm font-medium text-gray-700 flex items-center">
              <span className="w-1 h-6 bg-red-500 mr-2 rounded-sm"></span>
              <span className="flex flex-col">
                <span>Overall Budget</span>
                <span className="text-xs text-gray-500">(in numbers)</span>
              </span>
            </label>
            <div className="relative">
              <input
                type="text"
                name="rateInNumber"
                value={propertyInfo?.rateInNumber || ""}
                readOnly
                className="w-full px-4 py-2 input-focus bg-gray-50 cursor-not-allowed"
                placeholder={`Auto calculated (${
                  propertyInfo?.areaType === "sqft" ? "SQ.FT" :
                  propertyInfo?.areaType === "cent" ? "Cents" :
                  propertyInfo?.areaType === "ground" ? "Grounds" :
                  propertyInfo?.areaType === "acres" ? "Acres" : "SQ.FT"
                } × Rate per ${
                  propertyInfo?.areaType === "sqft" ? "SQ.FT" :
                  propertyInfo?.areaType === "cent" ? "Cent" :
                  propertyInfo?.areaType === "ground" ? "Ground" :
                  propertyInfo?.areaType === "acres" ? "Acres" : "SQ.FT"
                })`}
              />
            </div>
          </div>

          {/* Overall Area Range - Only for acres/cents and resale plot */}
          {shouldShowAreaRange() && (
            <div className="grid grid-cols-1 md:grid-cols-[160px_1fr] gap-6 items-center">
              <label className="text-sm font-medium text-gray-700">
                Overall Area Range
              </label>
              <input
                type="text"
                name="overAllAreaRange"
                value={propertyInfo?.overAllAreaRange || ""}
                onChange={handlePropertyInfoChange}
                className="w-full px-4 py-2 input-focus"
                placeholder="Enter Overall Area Range (e.g., 1000-2000)"
              />
            </div>
          )}

          {/* Overall Budget */}
          <div className="grid grid-cols-1 md:grid-cols-[160px_1fr] gap-6 items-center">
           <label className="text-sm font-medium text-gray-700 flex items-center">
                  <span className="w-1 h-6 bg-red-500 mr-2 rounded-sm"></span>
                  <span className="flex flex-col">
                    <span>Overall Budget</span>
                    <span className="text-xs text-gray-500">(in words)</span>
                  </span>
                </label>
            <input
              type="text"
              name="overAllBudget"
              value={propertyInfo?.overAllBudget || ""}
              onChange={(e) =>
                handlePropertyInfoChange({
                  target: { name: "overAllBudget", value: e.target.value },
                })
              }
              className="w-full px-4 py-2 input-focus"
              placeholder="Enter Overall Budget (in words)"
            />
          </div>
        </div>
      ),
    },
    {
      title: "Property Sourced By Info",
      content: (
        <div className="grid grid-cols-1 pb-32 md:grid-cols-2 gap-6 p-6">
          <div className="flex items-center gap-4">
            <label className="w-40 text-sm font-medium text-gray-700 flex items-center">
              <span className="w-1 h-6 bg-red-500 mr-2 rounded-sm"></span>
              Primary Source
            </label>
            <CustomSelect
              options={sourceBy}
              value={
                propertyInfo?.primarySourcedBy && sourceBy?.length > 0
                  ? sourceBy.find((s) => s.value === propertyInfo.primarySourcedBy) || null
                  : null
              }
              onChange={(selectedOption) =>
                handlePropertyInfoChange({
                  target: { name: "primarySourcedBy", value: selectedOption?.value || "" },
                })
              }
              placeholder="Select Property Source"
              className="flex-1"
              isSearchable
              dropdownHeight="130px"
               
            />
          </div>
          <div className="flex items-center gap-4">
            <label className="w-40 text-sm font-medium text-gray-700">
              Secondary Source
            </label>
            <CustomSelect
              options={sourceBy}
              value={
                propertyInfo?.secondarySourcedBy && sourceBy?.length > 0
                  ? sourceBy.find((s) => s.value === propertyInfo.secondarySourcedBy) || null
                  : null
              }
              onChange={(selectedOption) =>
                handlePropertyInfoChange({
                  target: { name: "secondarySourcedBy", value: selectedOption?.value || "" },
                })
              }
              placeholder="Select Property Source"
              className="flex-1"
              isSearchable
              dropdownHeight="130px"
            />
          </div>
        </div>
      ),
    },
    {
      title: "Vendor, Mediator & Trader Info",
      content: (
        <div className="space-y-6 p-6 pb-32">
          {[
            { label: "Vendor", name: "vendor" },
            { label: "Mediator", name: "mediator" },
            { label: "Trader", name: "trader" },
          ].map((person) => (
            <div key={person.name} className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-center gap-4">
                <label className="w-40 text-sm font-medium text-gray-700">
                  {person.label} Name
                </label>
                <input
                  type="text"
                  name={`${person.name}Name`}
                  value={propertyInfo?.[`${person.name}Name`] || ""}
                  onChange={handlePropertyInfoChange}
                  className="flex-1 px-4 py-2 input-focus"
                  placeholder={`Enter ${person.label} Name`}
                />
              </div>
              <div className="flex items-center gap-4">
                <label className="w-40 text-sm font-medium text-gray-700">
                  {person.label} Number
                </label>
                <div className="flex-1">
                  <PhoneInputField
                    value={
                      propertyInfo?.[`${person.name}CountryCode`] && propertyInfo?.[`${person.name}PhoneNumber`]
                        ? `${propertyInfo[`${person.name}CountryCode`]}${propertyInfo[`${person.name}PhoneNumber`]}`
                        : propertyInfo?.[`${person.name}PhoneNumber`] || ""
                    }
                    onChange={(value) => {
                      if (value && value.length > 10) {
                        // Extract country code and phone number from combined format
                        const phoneNumber = value.slice(-10); // Last 10 digits are phone number
                        const countryCode = value.slice(0, -10); // Everything before last 10 digits is country code
                        
                        // Update both country code and phone number
                        handlePropertyInfoChange({
                          target: { name: `${person.name}CountryCode`, value: countryCode },
                        });
                        handlePropertyInfoChange({
                          target: { name: `${person.name}PhoneNumber`, value: phoneNumber },
                        });
                      } else {
                        // Handle case where only country code is provided or empty
                        handlePropertyInfoChange({
                          target: { name: `${person.name}CountryCode`, value: value || "91" },
                        });
                        handlePropertyInfoChange({
                          target: { name: `${person.name}PhoneNumber`, value: "" },
                        });
                      }
                    }}
                    placeholder={`Enter ${person.label} Number`}
                    name={`${person.name}PhoneNumber`}
                    maxLength={15}
                    dropdownHeight="120px"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      ),
    },
    {
      title: "Address Info",
      content: (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6">
          <div className="w-full  rounded-md overflow-hidden border">
          <MapWithPropertyAndData propertyInfo={propertyInfo} />
          </div>
          <div className="space-y-4">
            <div className="flex items-center">
              <label className="w-[50%] block text-sm font-medium text-gray-700">
                <span className="border-red-400 border-l-4 mr-1 rounded-md"></span>
                Location
              </label>
              <div className="w-[50%]">
                <CustomSelect
                  options={locationData}
                  value={propertyInfo?.location || null}
                  onChange={(selectedOption) =>
                    handlePropertyInfoChange({
                      target: { name: "location", value: selectedOption || "" },
                    })
                  }
                  placeholder="Select Location"
                  isSearchable
                />
              </div>
            </div>
            <div className="flex items-center">
              <label className="w-[50%] block text-sm font-medium text-gray-700">
                Village
              </label>
              <div className="w-[50%]">
                <CustomSelect
                  options={locationData}
                  value={propertyInfo?.village || null}
                  onChange={(selectedOption) =>
                    handlePropertyInfoChange({
                      target: { name: "village", value: selectedOption || "" },
                    })
                  }
                  placeholder="Select village"
                  isSearchable
                />
              </div>
            </div>
            <div className="flex items-center">
              <label className="w-[50%] block text-sm font-medium text-gray-700">
                {getGeoLocationLabel()}
              </label>
              <input
                type="text"
                name="geoLocation"
                value={propertyInfo?.geoLocation || ""}
                onChange={(e) => {
                  const cleaned = e.target.value
                    .replace(/°/g, "")
                    .replace(/[NSEW]/gi, "")
                    .replace(/\s+/g, "");
                  handlePropertyInfoChange({
                    target: { name: "geoLocation", value: cleaned },
                  });
                }}
                placeholder="e.g., 12.9249, 80.1000"
                className="w-[50%] px-3 py-2 border rounded-md input-focus"
              />
            </div>
            <div className="flex items-center">
              <label className="w-[50%] block text-sm font-medium text-gray-700">
                Address
              </label>
              <textarea
                name="address"
                value={propertyInfo?.address || ""}
                onChange={handlePropertyInfoChange}
                placeholder="Address"
                className="w-[50%] h-20 px-3 py-2 border rounded-md input-focus"
              />
            </div>
            <div className="flex items-center">
              <label className="w-[50%] block text-sm font-medium text-gray-700">
                Landmark
              </label>
              <textarea
                name="landmark"
                rows={2}
                value={propertyInfo?.landmark || ""}
                onChange={handlePropertyInfoChange}
                placeholder="Near"
                className="w-[50%] px-3 py-2 border rounded-md input-focus"
              />
            </div>
            <div className="flex items-center">
              <label className="w-[50%] block text-sm font-medium text-gray-700">
                Location Link
              </label>
              <input
                type="text"
                name="locationLink"
                value={propertyInfo?.locationLink || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  handlePropertyInfoChange({
                    target: { name: "locationLink", value },
                  });
                }}
                onBlur={(e) => {
                  const value = e.target.value;
                  const gmapRegex =
                    /^(https?:\/\/)?(www\.)?(google\.[a-z.]+\/maps|goo\.gl\/maps)\/.+$/;
                  if (value && !gmapRegex.test(value)) {
                    showErrorToast("Please enter a valid Google Maps link");
                  }
                }}
                placeholder="Enter Google Maps link"
                className="w-[50%] px-3 py-2 border rounded-md input-focus"
              />
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "Media Info",
      content: (
        <div className="space-y-6 p-6">
          <div className="grid grid-cols-1 gap-6">
            <MediaUpload
              mediaType="Images"
              files={propertyInfo?.images || []}
              onChange={(urls) => handleArrayFieldChange("images", urls)}
              accept="image/*"
              maxFiles={5}
              folderpath="property_rough_documents"
            />
          </div>
          <div className="space-y-4">
            {[
              { label: "Drive Link", name: "driveLink" },
              { label: "Sketch Link", name: "sketchLink" },
              { label: "Attached Link", name: "attachedLink" },
            ].map((link) => {
              const value = propertyInfo?.[link.name] || "";
              const urlRegex = /^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w-./?%&=]*)?$/;
              const isInvalid = value && !urlRegex.test(value);
              
              return (
                <div key={link.name} className="flex flex-col gap-2">
                  <label className="w-40 text-sm font-medium text-gray-700 flex items-center">
                    {link.label} <IoIosLink className="inline-block mb-1 ml-1 text-blue-600" />
                  </label>
                  <input
                    type="url"
                    name={link.name}
                    value={value}
                    onChange={(e) => {
                      handlePropertyInfoChange({
                        target: { name: link.name, value: e.target.value },
                      });
                    }}
                    className={`flex-1 px-4 py-2 input-focus ${isInvalid ? 'border-red-500' : ''}`}
                    placeholder={`Enter ${link.label}`}
                  />
                  {isInvalid && (
                    <span className="text-red-500 text-xs">Please enter a valid {link.label}</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ),
    },
    {
      title: "Notes",
      content: (
        <div className="space-y-4 p-6">
          <div className="flex flex-col gap-2">
            <label className="w-40 text-sm font-medium text-gray-700">
              Notes
            </label>
            <textarea
              name="notes"
              rows={4}
              value={propertyInfo?.notes || ""}
              onChange={handlePropertyInfoChange}
              className="flex-1 px-4 py-2 input-focus"
              placeholder="Type your notes here..."
            />
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="w-full">
      <Accordion items={accordionItems} />
    </div>
  );
};

export default RoughPropertyInfoBasic;